import { useEffect, useState } from 'react';
import { api } from '../api';

const STATUS: Record<string, { label: string; color: string }> = {
  pending: { label: '待生产', color: 'bg-yellow-100 text-yellow-700' },
  'in-progress': { label: '生产中', color: 'bg-blue-100 text-blue-700' },
  completed: { label: '已完成', color: 'bg-green-100 text-green-700' },
  cancelled: { label: '已取消', color: 'bg-red-100 text-red-700' },
};

export default function Production() {
  const [list, setList] = useState<any[]>([]);
  const [prods, setProds] = useState<any[]>([]);
  const [deps, setDeps] = useState<any[]>([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState<any>(null);
  const [form, setForm] = useState({ product_id: '', plan_qty: '', department_id: '', remark: '' });

  const load = () => api('/production/orders').then(setList).catch(() => {});
  useEffect(() => {
    load();
    api('/products').then(setProds).catch(() => {});
    api('/departments').then(setDeps).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.product_id || !form.plan_qty) return alert('请填写产品与计划数量');
    const body: any = {
      product_id: Number(form.product_id),
      plan_qty: Number(form.plan_qty),
      department_id: Number(form.department_id) || null,
      remark: form.remark,
    };
    if (edit) {
      await api(`/production/orders/${edit.id}`, { method: 'PUT', body: JSON.stringify(body) });
    } else {
      await api('/production/orders', { method: 'POST', body: JSON.stringify(body) });
    }
    setShow(false); setEdit(null); reset(); load();
  };

  // 完工 → 自动入库
  const complete = async (id: number) => {
    const order = list.find((o) => o.id === id);
    const qty = prompt(`完工数量？（计划: ${order?.plan_qty}）`, String(order?.plan_qty || ''));
    if (!qty || Number(qty) <= 0) return;
    try {
      await api(`/production/orders/${id}/complete`, { method: 'POST', body: JSON.stringify({ done_qty: Number(qty) }) });
      load();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const del = async (id: number) => {
    if (!confirm('确认删除？')) return;
    await api(`/production/orders/${id}`, { method: 'DELETE' }); load();
  };

  const reset = () => setForm({ product_id: '', plan_qty: '', department_id: '', remark: '' });
  const openEdit = (o: any) => {
    setEdit(o);
    setForm({ product_id: String(o.product_id), plan_qty: String(o.plan_qty), department_id: String(o.department_id || ''), remark: o.remark || '' });
    setShow(true);
  };

  // 统计
  const totalPlan = list.reduce((s, o) => s + (o.plan_qty || 0), 0);
  const totalDone = list.reduce((s, o) => s + (o.done_qty || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">生产管理</h2>
          <p className="text-sm text-slate-500 mt-1">生产订单 → 完工 → 自动入库闭环</p>
        </div>
        <button onClick={() => { setEdit(null); reset(); setShow(true); }} className="bg-brand-600 text-white rounded-lg px-4 py-2 hover:bg-brand-700">
          + 新建生产单
        </button>
      </div>

      {/* 统计 */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-xl shadow p-4"><div className="text-xs text-slate-500">总工单</div><div className="text-xl font-bold">{list.length}</div></div>
        <div className="bg-white rounded-xl shadow p-4"><div className="text-xs text-slate-500">计划总量</div><div className="text-xl font-bold text-blue-600">{totalPlan}</div></div>
        <div className="bg-white rounded-xl shadow p-4"><div className="text-xs text-slate-500">已完成量</div><div className="text-xl font-bold text-green-600">{totalDone}</div></div>
        <div className="bg-white rounded-xl shadow p-4"><div className="text-xs text-slate-500">完成率</div><div className="text-xl font-bold text-purple-600">{totalPlan > 0 ? ((totalDone / totalPlan) * 100).toFixed(1) : 0}%</div></div>
      </div>

      {/* 列表 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-left">
              <th className="p-3">ID</th><th>产品</th><th>计划数量</th><th>已完成</th><th>状态</th><th>部门</th><th>备注</th><th className="w-28">操作</th>
            </tr>
          </thead>
          <tbody>
            {list.map((o) => (
              <tr key={o.id} className="border-t hover:bg-slate-50">
                <td className="p-3">#{o.id}</td>
                <td className="p-3 font-medium">{o.product_name || `产品#${o.product_id}`}</td>
                <td className="p-3">{o.plan_qty}</td>
                <td className="p-3"><span className={Number(o.done_qty || 0) >= Number(o.plan_qty) ? 'text-green-600 font-medium' : ''}>{o.done_qty || 0} / {o.plan_qty}</span></td>
                <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${STATUS[o.status]?.color || 'bg-gray-100'}`}>{STATUS[o.status]?.label || o.status}</span></td>
                <td className="p-3 text-xs"><span className="px-2 py-0.5 bg-slate-100 rounded">{o.department_name || '—'}</span></td>
                <td className="p-3 text-xs text-slate-400 max-w-[150px] truncate">{o.remark || '—'}</td>
                <td className="p-3 space-x-2">
                  {o.status !== 'completed' && <button onClick={() => openEdit(o)} className="text-brand-600 hover:underline text-xs">编辑</button>}
                  {o.status === 'pending' && <button onClick={() => complete(o.id)} className="text-green-600 hover:underline text-xs font-medium">完工入库</button>}
                  {['pending', 'in-progress'].includes(o.status) && <button onClick={() => del(o.id)} className="text-red-500 hover:underline text-xs">删除</button>}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">暂无生产单</td></tr>}
          </tbody>
        </table>
      </div>

      {/* 弹窗 */}
      {show && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShow(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">{edit ? '编辑生产单' : '新建生产单'}</h3>
            <div className="space-y-3">
              <select className="w-full border rounded-lg px-3 py-2" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
                <option value="">选择产品</option>
                {prods.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code})</option>)}
              </select>
              <input className="w-full border rounded-lg px-3 py-2" type="number" placeholder="计划数量 *" value={form.plan_qty} onChange={(e) => setForm({ ...form, plan_qty: e.target.value })} />
              <select className="w-full border rounded-lg px-3 py-2" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
                <option value="">选择部门</option>
                {deps.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <input className="w-full border rounded-lg px-3 py-2" placeholder="备注" value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShow(false)} className="px-4 py-2 border rounded-lg hover:bg-slate-50">取消</button>
              <button onClick={save} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
